import axios, { AxiosInstance } from 'axios';
// import api from './api';

export interface SignUpRequest {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  phone?: string;
  acceptTerms: boolean;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetConfirm {
  token: string;
  newPassword: string;
}

export interface EmailVerificationRequest {
  email: string;
}

export interface AuthResponse {
  success: boolean;
  message?: string;
  data?: {
    user: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      emailVerified?: boolean;
      roles: Array<{
        id: string;
        name: string;
        permissions: Array<{
          id: string;
          name: string;
          resource: string;
          action: string;
        }>;
      }>;
    };
    tokens: {
      accessToken: string;
      refreshToken: string;
      expiresIn: string;
    };
  };
  error?: string;
  errors?: Array<{
    field?: string;
    message: string;
  }>;
}

export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
  errors?: Array<{
    field?: string;
    message: string;
  }>;
}

class EnhancedAuthService {
  private api: AxiosInstance;

  constructor() {
    // Normalize the configured auth gateway URL. Accept either
    // - full gateway path including `/auth` (e.g. http://localhost:8000/api/auth)
    // - or base API gateway URL (e.g. http://localhost:8000/api)
    // and ensure we end up with the API gateway base (no trailing /auth)
    const rawAuthUrl = import.meta.env.VITE_AUTH_API_URL as string | undefined;
    const gatewayBase = rawAuthUrl
      ? rawAuthUrl.replace(/\/auth\/?$/i, "")
      : '/api';

    this.api = axios.create({
      baseURL: gatewayBase,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token interceptor
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle auth errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          this.logout();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Sign up a new user
   */
  /**
   * Sign up a new user
   */
  async signUp(data: SignUpRequest): Promise<ApiResponse> {
  const payload = {
    email: data.email,
    password: data.password,
    firstName: data.firstName,
    lastName: data.lastName,
    phone: data.phone // include ONLY if backend supports it
  };

  const response = await this.api.post('/auth/signup', payload);

  if (response.data && typeof response.data.success === 'undefined') {
    return {
      success: response.status >= 200 && response.status < 300,
      message: response.data?.message || '',
      data: response.data,
    };
  }

  return response.data;
}

  /**
   * Login user
   */
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await this.api.post('/auth/login', credentials);
    
    if (response.data.success && response.data.data) {
      // Store tokens and user data
      localStorage.setItem('authToken', response.data.data.tokens.accessToken);
      localStorage.setItem('refreshToken', response.data.data.tokens.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.data.data.user));
    }
    
    return response.data;
  }

  /**
 * Request password reset (Send OTP or reset email)
 */
async requestPasswordReset(data: PasswordResetRequest): Promise<ApiResponse> {
  try {
    const response = await this.api.post('/auth/forgot-password', data);

    return {
      success: response.data.success ?? response.status === 200,
      message: response.data.message ?? "Reset mail sent",
      data: response.data.data
    };
  } catch (err:any) {
    return {
      success:false,
      error: err.response?.data?.message || "Failed to send reset password email"
    };
  }
}

/**
 * Reset password using OTP
 */
async resetPassword(data: { email:string; otp:string; newPassword:string }): Promise<ApiResponse> {
  try {
    const response = await this.api.post('/auth/reset-password', data);

    return {
      success: response.data.success ?? response.status === 200,
      message: response.data.message ?? "Password reset successful",
      data: response.data.data
    };
  } catch (err:any) {
    return {
      success:false,
      error: err.response?.data?.message || "OTP incorrect or expired"
    };
  }
}

async verifyResetOtp(data: { email:string; otp:string }): Promise<ApiResponse> {
  try {
    const response = await this.api.post('/auth/verify-reset-otp', data);

    return {
      success: response.data?.success ?? response.status === 200,
      message: response.data?.message ?? "OTP verified successfully",
      data: response.data?.data
    };
  } catch (err:any) {
    return {
      success:false,
      error: err.response?.data?.message || "Invalid or expired OTP"
    };
  }
}


  /**
   * Verify email address
   */
  async verifyEmail(token: string): Promise<ApiResponse> {
    const response = await this.api.get(`/auth/verify-email/${token}`);
    return response.data;
  }

  /**
   * Verify OTP (mobile/email) endpoint
   * Backend route: POST /api/v1/auth/verifyUserOtp
   */
  async verifyUserOtp(data: any): Promise<ApiResponse> {
    // normalize { identifier, otp } -> { email, otp } for backend
    const payload: any = { ...data };
    if (!payload.email && payload.identifier) {
      payload.email = payload.identifier;
      delete payload.identifier;
    }

    try {
      const relativePath = '/auth/verifyUserOtp';
      const target = `${this.api.defaults.baseURL}${relativePath}`;
      console.debug('[enhancedAuth] verifyUserOtp ->', target, payload);

      const response = await this.api.post(relativePath, payload);
      console.debug('[enhancedAuth] verifyUserOtp response ->', response.status, response.data);

      // Normalize response shape: if backend didn't return `success`, set it based on HTTP status
      if (response.data && typeof response.data.success === 'undefined') {
        return {
          success: response.status >= 200 && response.status < 300,
          message: response.data?.message || '',
          data: response.data,
        } as ApiResponse;
      }

      return response.data;
    } catch (err: any) {
      console.error('[enhancedAuth] verifyUserOtp error ->', err?.response?.status, err?.response?.data || err.message);
      // If gateway returned 404 (route not found) try a direct request
      // to the auth service (helpful in local dev when gateway routing
      // isn't running or misconfigured). This fallback is safe because
      // the auth service allows localhost origins in development.
      if (err?.response?.status === 404) {
        try {
          const axios = (await import('axios')).default;
          const directUrl =
            (import.meta.env.VITE_AUTH_DIRECT_URL as string) ||
            'http://localhost:4001/api/v1/auth/verifyUserOtp';
          console.debug('[enhancedAuth] verifyUserOtp fallback ->', directUrl, payload);
          const resp = await axios.post(directUrl, payload, { timeout: 10000 });
          console.debug('[enhancedAuth] verifyUserOtp fallback response ->', resp.status, resp.data);

          if (resp.data && typeof resp.data.success === 'undefined') {
            return {
              success: resp.status >= 200 && resp.status < 300,
              message: resp.data?.message || '',
              data: resp.data,
            } as ApiResponse;
          }

          return resp.data as ApiResponse;
        } catch (innerErr: any) {
          console.error('[enhancedAuth] verifyUserOtp fallback error ->', innerErr?.response?.status, innerErr?.response?.data || innerErr.message);
          return Promise.reject(innerErr);
        }
      }

      return Promise.reject(err);
    }
  }

  
  /**
   * Resend OTP endpoint
   * Backend route: POST /api/v1/auth/resendOtp
   */
  async resendOtp(data: any): Promise<ApiResponse> {
    // normalize { identifier } -> { email }
    const payload: any = { ...data };
    if (!payload.email && payload.identifier) {
      payload.email = payload.identifier;
      delete payload.identifier;
    }

    try {
      const relativePath = '/auth/resendOtp';
      const target = `${this.api.defaults.baseURL}${relativePath}`;
      console.debug('[enhancedAuth] resendOtp ->', target, payload);

      const response = await this.api.post(relativePath, payload);
      console.debug('[enhancedAuth] resendOtp response ->', response.status, response.data);

      if (response.data && typeof response.data.success === 'undefined') {
        return {
          success: response.status >= 200 && response.status < 300,
          message: response.data?.message || '',
          data: response.data,
        } as ApiResponse;
      }

      return response.data;
    } catch (err: any) {
      console.error('[enhancedAuth] resendOtp error ->', err?.response?.status, err?.response?.data || err.message);
      if (err?.response?.status === 404) {
        try {
          const axios = (await import('axios')).default;
          const directUrl =
            (import.meta.env.VITE_AUTH_DIRECT_URL as string) ||
            'http://localhost:4001/api/v1/auth/resendOtp';
          console.debug('[enhancedAuth] resendOtp fallback ->', directUrl, payload);
          const resp = await axios.post(directUrl, payload, { timeout: 10000 });
          console.debug('[enhancedAuth] resendOtp fallback response ->', resp.status, resp.data);

          if (resp.data && typeof resp.data.success === 'undefined') {
            return {
              success: resp.status >= 200 && resp.status < 300,
              message: resp.data?.message || '',
              data: resp.data,
            } as ApiResponse;
          }

          return resp.data as ApiResponse;
        } catch (innerErr: any) {
          console.error('[enhancedAuth] resendOtp fallback error ->', innerErr?.response?.status, innerErr?.response?.data || innerErr.message);
          return Promise.reject(innerErr);
        }
      }

      return Promise.reject(err);
    }
  }

  /**
   * Resend email verification
   */
  async resendEmailVerification(data: EmailVerificationRequest): Promise<ApiResponse> {
    const response = await this.api.post('/auth/resend-verification', data);
    return response.data;
  }

  /**
   * Get current user profile
   */
  async getProfile(): Promise<ApiResponse> {
    const response = await this.api.get('/auth/profile');
    return response.data;
  }

  /**
   * Refresh access token
   */
  async refreshToken(): Promise<AuthResponse> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await this.api.post('/auth/refresh-token', {
      refreshToken,
    });

    if (response.data.success && response.data.data) {
      localStorage.setItem('authToken', response.data.data.accessToken);
      localStorage.setItem('refreshToken', response.data.data.refreshToken);
    }

    return response.data;
  }

  /**
   * Logout user
   */
  logout(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }

  /**
   * Check if user is logged in
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem('authToken');
  }

  /**
   * Get current user from storage
   */
  getCurrentUser(): any | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  /**
   * Check if user has specific permission
   */
  hasPermission(resource: string, action: string): boolean {
    const user = this.getCurrentUser();
    if (!user?.roles) return false;

    return user.roles.some((role: any) =>
      role.permissions.some((permission: any) =>
        permission.resource === resource && permission.action === action
      )
    );
  }

  /**
   * Check if user has specific role
   */
  hasRole(roleName: string): boolean {
    const user = this.getCurrentUser();
    if (!user?.roles) return false;

    return user.roles.some((role: any) => role.name === roleName);
  }
}

export default new EnhancedAuthService();